import React, { Component } from 'react';
import { Redirect } from 'react-router';

import * as analytics from '@opendoor/shared-fe/build/analytics';
import { Query } from '@apollo/client/react/components';
import { css, Global } from '@emotion/react';
import { Text } from '@opendoor/bricks/core';
import DashboardLayoutBody, {
  IDashboardLayoutBodyProps,
} from '@opendoor/bricks/deprecated/UNSAFE_layout/DashboardLayoutBody';
import DashboardLayoutWrapper, {
  IDashboardLayoutWrapperProps,
} from '@opendoor/bricks/deprecated/UNSAFE_layout/DashboardLayoutWrapper';
import SessionApi from '@opendoor/shared-fe/build/api/session';
import { GraphQLError } from 'graphql';

import PartnerSpecificHomeLink from '../components/PartnerSpecificHomeLink';
import { SessionContext } from '../config/context';
import { GET_SESSION_DATA, IGetSessionData } from '../graphql/queries/GetSessionData';
import { WEB_API_URL } from '../helpers/globals';
import { RedirectToLogin } from '../routes/PrivateRoute';
import { ERROR_PAGE_ROUTE } from '../routes/routes';
import style from '../styles/LayoutStyle.less';

export const hasAnyUnauthenticatedError = (errors: ReadonlyArray<GraphQLError>): boolean => {
  for (const gqlError of errors) {
    if (gqlError.extensions?.code === 'UNAUTHENTICATED') {
      return true;
    }
  }
  return false;
};

export type LayoutProps = IDashboardLayoutBodyProps &
  Pick<
    IDashboardLayoutWrapperProps,
    Exclude<keyof IDashboardLayoutWrapperProps, 'renderNav' | 'analyticsName'>
  > & {
    renderNav?: (el?: HTMLElement) => React.ReactNode;
  };

const getAccountInfo = (
  data: undefined | IGetSessionData,
): IDashboardLayoutWrapperProps['accountInfo'] => {
  if (!data) {
    return undefined;
  }
  const { session } = data;
  if (!session || !session.human) {
    return undefined;
  }

  return {
    email: session.human.email,
    hasResaleClosingDashboard: session.human.has_resale_closing_dashboard,
    hasPartnershipReferralDashboard: session.human.has_partnership_referral_dashboard,
    hasPartnershipReportingDashboard: session.human.has_partnership_reporting_dashboard,
    hasPartnershipDeveloperDashboard: session.human.has_partnership_developer_dashboard,
    hasPartnershipNotificationPreferencesPage: session.human.has_partnership_referral_dashboard,
    leads: [],
    name: session.human.first_name || session.human.full_name,
  };
};

const parseHumanId = (data: undefined | IGetSessionData): string => {
  if (data?.session?.human?.id) {
    return 'Human-' + data.session.human.id;
  }
  return '';
};

export class Layout extends Component<LayoutProps> {
  public logOut = async (refetch: VoidFunction, session: SessionApi) => {
    await session.logOut();
    refetch();
  };

  public renderLogo = () => (
    <div className={style['logo-container']}>
      <PartnerSpecificHomeLink>
        <img
          className={`opendoor-logo ${style.logo}`}
          src="https://imgdrop.imgix.net/logo-opendoor.svg"
          alt="Opendoor"
          height="25"
        />
      </PartnerSpecificHomeLink>
      {/* eslint-disable-next-line react/jsx-pascal-case */}
      <Text fontSize="s0" lineHeight="s00" color="neutrals90" margin="auto" pl={6}>
        Partners
      </Text>
    </div>
  );

  public renderNav = () => {
    const { renderNav } = this.props;
    return renderNav ? renderNav(document.body) : null;
  };

  public render() {
    const { renderContent, renderSide } = this.props;
    return (
      <SessionContext.Consumer>
        {(session) => {
          return (
            <Query<IGetSessionData> query={GET_SESSION_DATA}>
              {({ data, error, loading, refetch }) => {
                if (error && window.location.pathname !== ERROR_PAGE_ROUTE) {
                  if (hasAnyUnauthenticatedError(error?.graphQLErrors)) {
                    return <RedirectToLogin />;
                  }
                  return <Redirect to={ERROR_PAGE_ROUTE} />;
                }
                const accountInfo = getAccountInfo(data);
                const humanId = parseHumanId(data);
                if (humanId) {
                  analytics.registerWithUserID(humanId, {
                    email: accountInfo && accountInfo.email,
                  });
                } else {
                  analytics.register({ email: accountInfo && accountInfo.email });
                }
                return (
                  <DashboardLayoutWrapper
                    analyticsName="partnership-dashboard-fe"
                    accountInfo={accountInfo}
                    isLoadingAccountInfo={loading}
                    onLogout={this.logOut.bind(this, refetch, session)}
                    opendoorDomain={WEB_API_URL}
                    opendoorPartnershipsDomain=""
                    renderLogo={this.props.renderLogo || this.renderLogo}
                    renderNav={this.renderNav}
                  >
                    <DashboardOverrideStyles />
                    <DashboardLayoutBody renderContent={renderContent} renderSide={renderSide} />
                  </DashboardLayoutWrapper>
                );
              }}
            </Query>
          );
        }}
      </SessionContext.Consumer>
    );
  }
}

const DashboardOverrideStyles = () => {
  return (
    <Global
      styles={css`
        padding-top: 0 !important;
        padding-bottom: 0 !important;

        .row {
          width: 100%;
          margin: auto;
        }

        .od-dashboard-layout-body__center {
          padding: 0;
        }

        .container {
          width: 100%;
        }

        .od-dashboard-layout-body-side {
          padding: 0 24px !important;
        }

        .icon-nav-bar-icon path {
          stroke: black;
        }
        .icon-nav-bar-icon rect {
          stroke: black;
        }
      `}
    />
  );
};

export const ReportingDashboardOverrideStyles = () => {
  return (
    <Global
      styles={css`
        .od-dashboard-layout-wrapper__content {
          width: 2000px;
        }
        .od-dashboard-layout-wrapper__children {
          max-width: 1600px;
        }
      `}
    />
  );
};

export const ReferralDashboardOverrideStyles = () => {
  return (
    <Global
      styles={css`
        .od-dashboard-layout-wrapper__content {
          width: 100%;
          max-width: 1500px;
        }
        .od-dashboard-layout-wrapper__children {
          max-width: none;
        }
      `}
    />
  );
};

export default Layout;
